import {HttpHeaders, HttpParams} from '@angular/common/http';
import {Component} from '@angular/core';
import {ComponentBase} from '@drl/core/component-base';
import {AuthTokenSkipHeader} from '@drl/core/interceptors/auth.interceptor';
import {UserSessionStoreService as StoreService} from '@drl/core/store/user-session-store.service';
import {NbIconLibraries} from '@nebular/theme';

import {FontPack} from '@drl/core/enums';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import * as FullStory from '@fullstory/browser';
import {environment} from '../environments/environment';
import {PasswordExpiry} from './password-expiry-popup/services/password-expiry.service';
import {PasswordExpiryEmitter} from './password-expiry-popup/services/password-expiry-emitter.service';
import {Meta, Title} from '@angular/platform-browser';
import {ConfigurationsFacadeService} from './shared/facades';
import {TranslateService} from '@ngx-translate/core';
import {GetTenantProfileCommand} from './core/store/commands';
import {TenantDetails} from './shared/models';
import {ApiService} from './core/api/api.service';
import {TenantProfileAdapter} from './shared/adapters';
import * as moment from 'moment';

const loadingTenantDetailsLabel = 'loadingTenantDetails';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: [],
})
export class AppComponent extends ComponentBase {
  private readonly authHeaders = new HttpHeaders().set(AuthTokenSkipHeader, '');
  constructor(
    private readonly store: StoreService,
    private readonly iconLibraries: NbIconLibraries,
    public readonly ipsTranslate: IpsTranslateService,
    private readonly passwordExpiryEmitterService: PasswordExpiryEmitter,
    private readonly configFacade: ConfigurationsFacadeService,
    private readonly apiService: ApiService,
    private readonly tenProfileAdapter: TenantProfileAdapter,
    private readonly titleService: Title,
    // sonarignore:start
    private meta: Meta, // sonarignore:end
  ) {
    super();
    this.setTitleandImage();
    this.iconLibraries.registerFontPack(FontPack.FontAwesomeSolid, {
      packClass: 'fas',
      iconClassPrefix: 'fa',
    });
    this.iconLibraries.registerFontPack(FontPack.FontAwesomeRegular, {
      packClass: 'far',
      iconClassPrefix: 'fa',
    });
    if (environment.orgId) {
      FullStory.init({
        orgId: environment.orgId,
        debug: environment.debug,
        namespace: environment.namespace,
        recordCrossDomainIFrames: true,
        recordOnlyThisIFrame: false,
        devMode: !environment.production,
      });
    }
    this.translate = this.ipsTranslate.translate;
  }
  tenantName: string;
  passwordExpiry: PasswordExpiry;
  showPopup = false;
  translate: TranslateService;
  loading: boolean;
  loadingTenantDetails;

  async ngOnInit() {
    this.createCspMetaTag();
    await this.loadTranslations();
    await this.loadTenantDetails();
    moment.locale(this.store.getLocale());
    this._subscriptions.push(
      this.passwordExpiryEmitterService.passwordExpiryTypeEmitter.subscribe(
        data => {
          this.passwordExpiry = data;
          this.showPopup = true;
        },
      ),
    );
  }
  closePopup() {
    this.showPopup = false;
  }

  createCspMetaTag() {
    this.meta.addTag(
      {
        'http-equiv': 'Content-Security-Policy',
        // tslint:disable-next-line: max-line-length
        content: `default-src 'self' ${environment.cspTenantApiUrl} https://*.netcoresmartech.com https://*.hansel.io; script-src 'self' 'unsafe-inline' 'unsafe-eval' ${environment.cspTenantApiUrl} https://*.netcoresmartech.com https://*.hansel.io; script-src-elem 'self' 'unsafe-inline' 'unsafe-eval' ${environment.cspTenantApiUrl} https://*.netcoresmartech.com https://*.hansel.io; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://*.netcoresmartech.com https://*.hansel.io; img-src 'self' data: https://*.amazonaws.com; media-src 'self' data: https://*.amazonaws.com; connect-src 'self' 'unsafe-inline' ${environment.cspTenantApiUrl} https://*.pndsn.com https://*.netcoresmartech.com wss://*.hansel.io https://*.hansel.io ws://*.hansel.io; font-src 'self' https://fonts.gstatic.com https://*.hansel.io https://*.netcoresmartech.com;`,
      },
      true,
    );
  }
  setTitleandImage() {
    this.titleService.setTitle(environment.brand);
    document
      .getElementById('appFavicon')
      .setAttribute(
        'href',
        `assets/${environment.brand.toLocaleLowerCase()}-favicon.ico`,
      );
  }
  getAndSaveSubdomain() {
    const getSubdomainRegex = /(?:http[s]*\:\/\/)*(.*?)\.(?=[^\/]*\..{2,5})/i;
    const fullURL: string = window.location.host;
    const result: RegExpMatchArray = fullURL.match(getSubdomainRegex);
    let subdomain: string;

    if (result !== null) {
      subdomain = result[1];
      if (subdomain !== 'www') {
        this.store.setTenantKeyName(subdomain);
      }
    }
  }

  async loadTranslations() {
    this.ipsTranslate.init(this.store.getLocale());
    this.loadingTenantDetails = await this.translate
      .get(loadingTenantDetailsLabel)
      .toPromise()
      .then();
  }

  async loadTenantDetails() {
    this.getAndSaveSubdomain();

    this.loading = true;

    if (this.store.getTenantKeyName()) {
      this._subscriptions.push(
        this.configFacade
          .getTenantDetailsByTenantKey(this.store.getTenantKeyName())
          .subscribe((res: TenantDetails) => {
            this.store.setTenantName(res.name);
            this.store.setTenantId(res.id);
            environment.pubKey = res.pubKey;
            environment.subKey = res.subKey;
            this.getTenantProfile(res);
            this.loading = false;
          }),
      );
    }
  }

  getTenantProfile(tenantDetails: TenantDetails) {
    const command: GetTenantProfileCommand<TenantDetails> = new GetTenantProfileCommand(
      this.apiService,
      this.tenProfileAdapter,
    );

    command.parameters = {
      headers: this.authHeaders,
      query: new HttpParams().set('tenantName', tenantDetails.name),
    };

    this._subscriptions.push(
      command.execute().subscribe(profile => {
        this.translate.setDefaultLang(profile.locale);
        this.store.setCountryName(profile.country || 'India');
        this.store.setWhiteLabelled(profile.is_white_labelled || false);
        this.store.setTenantLogo(profile.logo);
        this.store.setMonochromaticTenantLogo(profile.monochrome_logo);
        this.loading = false;
      }),
    );
  }
}
